import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gamesRegistry } from '../data/gamesRegistry';
import gamesFeed from '../data/gamesFeed.json';
import { useHubStore } from '../store/useHubStore';
import { CategoryRow } from './CategoryRow';
import { RandomGameCard } from './RandomGameCard';

export const HubHome: React.FC = () => {
  const { setActiveGame, wishlist, manualGames } = useHubStore();

  const getGameData = (id: string) => {
    let game = (gamesFeed as any[]).find(g => g.id === id);
    if (!game) {
      if (id.endsWith('-gm')) {
        const parts = id.split('-');
        if (parts.length >= 3) {
          const gmId = parts[parts.length - 2];
          const titleSlug = parts.slice(0, parts.length - 2).join(' ');
          game = {
            id: id,
            title: titleSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            category: 'Game',
            thumbnail: `https://img.gamemonetize.com/${gmId}/512x384.jpg`,
            path: `/game/${id}`
          };
        }
      } else if (!isNaN(Number(id))) {
        // Raw GM ID fallback
        game = {
          id: `game-${id}-gm`,
          title: `Game ${id}`,
          category: 'Featured',
          thumbnail: `https://img.gamemonetize.com/${id}/512x384.jpg`,
          path: `/game/game-${id}-gm`
        };
      }
    }
    // ensure path is set
    if (game && !game.path) game.path = `/game/${game.id}`;
    return game;
  };

  const wishlistGames = wishlist.map(getGameData).filter(Boolean) as any[];
  const manualGamesList = manualGames.map(getGameData).filter(Boolean) as any[];

  return (
    <div style={{ 
      width: '100%',
      padding: '20px 0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <Helmet>
        <title>Gamezolve | Free Online Games - Play Now</title>
        <meta name="description" content="Play the best free online games directly in your browser on Gamezolve. No downloads, no logins, no popups. Action, Puzzle, Arcade, and more!" />
        <meta name="keywords" content="free online games, play games online, html5 games, browser games, gamezolve" />
        <meta property="og:title" content="Gamezolve | Free Online Games" />
        <meta property="og:description" content="Play the best free online games directly in your browser on Gamezolve. No downloads, no logins, no popups." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <style>
        {`
          .game-tile-overlay {
            opacity: 0;
          }
          .game-tile:hover .game-tile-overlay {
            opacity: 1 !important;
          }
          .game-tile:hover img {
            transform: scale(1.05) !important;
          }
          
          /* Responsive Spans */
          @media (min-width: 600px) {
            .span-2x2 {
              grid-column: span 2;
              grid-row: span 2;
            }
          }
          @media (min-width: 900px) {
            .span-3x3 {
              grid-column: span 3;
              grid-row: span 3;
            }
          }
        `}
      </style>
      
      <div style={{ maxWidth: '1200px', width: '100%', margin: '20px auto 0', padding: '0 20px' }}>
        {wishlistGames.length > 0 && (
          <CategoryRow title="❤️ My Wishlist" games={wishlistGames} />
        )}
        
        {manualGamesList.length > 0 && (
          <CategoryRow title="⭐ Handpicked For You" games={manualGamesList} />
        )}

        <CategoryRow title="⚔️ Action Packed" games={gamesFeed.filter((g: any) => g.category?.toLowerCase() === 'action')} />
        <CategoryRow title="🗺️ Epic Adventures" games={gamesFeed.filter((g: any) => g.category?.toLowerCase() === 'adventure')} />
        <CategoryRow title="🏎️ Racing & Driving" games={gamesFeed.filter((g: any) => ['racing', 'driving', 'car'].includes(g.category?.toLowerCase()))} />
        <CategoryRow title="🧩 Puzzle & Brain" games={gamesFeed.filter((g: any) => g.category?.toLowerCase() === 'puzzle')} />
        <CategoryRow title="🕹️ Arcade Classics" games={gamesFeed.filter((g: any) => g.category?.toLowerCase() === 'arcade')} />
        <CategoryRow title="⚽ Sports & Casual" games={gamesFeed.filter((g: any) => ['sports', 'casual', 'ball'].includes(g.category?.toLowerCase()))} />
        <CategoryRow title="♟️ Strategy & Board" games={gamesFeed.filter((g: any) => ['strategy', 'board'].includes(g.category?.toLowerCase()))} />
        <CategoryRow title="🏆 Top Rated Popular" games={gamesFeed.slice(0, 20)} />
        
        <RandomGameCard />
      </div>

      <div style={{ maxWidth: '1200px', margin: '60px auto 0', padding: '0 20px', color: '#4b5563', lineHeight: '1.6' }}>
        <h2 style={{ fontSize: '1.8rem', color: '#111827', marginBottom: '15px' }}>Play the Best Online Games on Gamezolve</h2>
        <p style={{ marginBottom: '15px' }}>Gamezolve brings you the best free online games. You can play our games on desktop, mobile, and tablet. We offer a curated collection of premium puzzle, arcade, and board games, now featuring thousands of hits from top developers.</p>
        <p>No downloads, no logins, no popups. Just click and play instantly!</p>
      </div>
    </div>
  );
};
